//-----------------------------------------------------------------------------
//
// header.js
//
// Copyright 2022 Emergence Software Pte. Ltd. - All rights reserved.
//
//-----------------------------------------------------------------------------

// Imports
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import PropTypes from "prop-types"
import { Link } from "gatsby";
import { RiArrowDropDownLine, RiEdit2Fill, RiFlightTakeoffFill, RiLinkedinBoxFill, RiLogoutBoxRLine, RiShareForwardFill, RiUser3Fill } from "react-icons/ri";
import { menuStructure, menuIcons } from "./menu-structure.js"
import { AuthService, isBrowser } from "../services/authentication/auth.browser"

// Definitions
const SMALL_SCREEN_SIZE = 750;
const MIN_SCREEN_SIZE = 535;
const MAX_LEVELS = 10;
const MENU_MAIN = 0;
const MENU_POPUP = 1;
const MENU_SMALL = 2;
const POPUP_TIMEOUT = 250;
const LOAD_ANIMATION_TIME_WINDOW = 500;


//-----------------------------------------------------------------------------
//
// SUPPORT FUNCTIONS
//
//-----------------------------------------------------------------------------

// Function InternalLink
export const InternalLink = ({ children, className, url, name="" }) =>
{
  return <Link className={className}
               aria-label={name}
               to={url}>
           {children}
         </Link>
}


// Function ExternalLink
export const ExternalLink = ({ children, className, url, name="" }) =>
{
  return <a href={url}
            className={className}
            aria-label={name}
            target="_blank"
            rel="noreferrer nofollow" >
           {children}
         </a>
}


//-----------------------------------------------------------------------------
//
// HEADER
//
//-----------------------------------------------------------------------------

// Function EmergenceHeader
const EmergenceHeader = React.memo(({ title, titleChange=undefined, noTitleBlock=false }) =>
{
  //-----------------------------------------------------------------------------
  //
  // INITIALIZATION
  //
  //-----------------------------------------------------------------------------

  // States
  const [focusStyle, setFocusStyle] = useState("")
  const [menuSmallExpanded, setMenuSmallExpanded] = useState(false);
  const [menuSelected, setMenuSelected] = useState("");
  const [pageSelected, setPageSelected] = useState("");
  const [pageLoaded, setPageLoaded] = useState(false);
  const [currentUser, setCurrentUser] = useState(undefined);
  const popupTimer = useRef(undefined);

  // References
  const menuHovering = useRef("")
  const menuPopupList = useRef([])

  // Startup effect read user from local storage if any
  useEffect(() =>
  {
    const user = isBrowser() ? AuthService.getCurrentUser() : undefined;
    if (user) setCurrentUser(user)
  }, [])


  // Startup effect check first load to handle animation quirks
  useEffect(() =>
  {
    setTimeout(() => { setPageLoaded(true); }, LOAD_ANIMATION_TIME_WINDOW)
  }, [])


  //-----------------------------------------------------------------------------
  //
  // SUPPORT FUNCTIONS
  //
  //-----------------------------------------------------------------------------

  // Function createFlatMenu
  const createFlatMenu = (menu, levels) =>
  {
    // Quick return on error
    if (!menu) return []

    // Recursively parse list
    let flat = []
    menu.forEach((item) => {
      flat.push(item)
      if (item.children && (levels > 1)) {
        flat = flat.concat(createFlatMenu(item.children, levels - 1))
      }
    })

    return flat
  }
  const menuFlat = useRef(createFlatMenu(menuStructure, MAX_LEVELS));
  const menuFlatTwoLevels = useRef(createFlatMenu(menuStructure, 2));


  //-----------------------------------------------------------------------------
  //
  // CALLBACKS
  //
  //-----------------------------------------------------------------------------

  // Register to URL changes
  useEffect(() => {
      let url = isBrowser() ? window.location.pathname : "/"
      let parent = undefined
      let selected = undefined

      // Set selected menu item if a match found and its not already selected
      menuFlatTwoLevels.current.forEach((item) => {
        // Match the URL string with entries in the menu. Home page ('/') is a special case
        // that will match every page so have to filter that out
        if (((item.url.length > 1)   && (url.includes(item.url))) ||
            ((item.url.length === 1) && (url.length === 1)))
        {
          // Set main menu item this page belongs to (could be the same as the page, or a category)
          if (item.parent) parent = item.parent
          else             parent = item.name

          // Set page
          selected = item.name

          // Break out
          if (selected) return;
        }
      })

      // Update selection if it has changed
      if (menuSelected !== parent)   setMenuSelected(parent)
      if (pageSelected !== selected) setPageSelected(selected)
  }, [menuFlat, menuSelected, pageSelected])


  // Register to screen width changes
  useLayoutEffect(() => {
    const changeWidth = () => {
                                // Apply small screen (mobile) scaling clamping if required
                                let viewport_meta_tag = document.querySelector("meta[name=viewport]");
                                if (viewport_meta_tag)
                                {
                                  // Calculate ratio
                                  let ww = isBrowser() ? window.screen.width : 0;
                                  let mw = MIN_SCREEN_SIZE;
                                  let ratio =  (ww / mw).toFixed(3);

                                  // Set viewport meta tag
                                  if (ww < mw) viewport_meta_tag.setAttribute('content', 'width=device-width, user-scalable=yes, initial-scale=1.0, minimum-scale=' + ratio);
                                  else         viewport_meta_tag.setAttribute('content', 'width=device-width, user-scalable=yes, initial-scale=1.0, minimum-scale=1.0');

                                  // Hide expanded small menu if we go from small to large
                                  let innerWidth = isBrowser() ? window.innerWidth : 0;
                                  if (innerWidth > SMALL_SCREEN_SIZE) {
                                    setMenuSmallExpanded(false);
                                  }
                                }
                              }

    changeWidth();
    isBrowser() && window.addEventListener('resize', changeWidth)
    return () => { isBrowser() && window.removeEventListener('resize', changeWidth) }
  }, [])


  // Register to mouse click handling
  useEffect(() => {
    document.addEventListener("click", () => { if (menuSmallExpanded) setMenuSmallExpanded(false) } );
    return () => {
      document.removeEventListener("click", () => { if (menuSmallExpanded) setMenuSmallExpanded(false) } );
    };
  }, [menuSmallExpanded])


  // Callback to handle immediate or delayed popup show/hide events
  const menuPopupHandler = useCallback((event, name, delayed) =>
  {
    // Reset timer (if any)
    if (popupTimer.current) clearTimeout(popupTimer.current)
    popupTimer.current = undefined

    // Delay the change of the popup dialog (with the chance to abort)
    if (delayed)
    {
      function handler() {
        menuHovering.current = name
        for (let i = 0; i < menuPopupList.current.length; i++) {
          menuPopupList.current[i].setShow(menuPopupList.current[i].name === name)
        }
      }

      popupTimer.current = setTimeout(handler, POPUP_TIMEOUT);
    }

    // Immediately set the popup dialog
    else
    {
      menuHovering.current = name
      for (let i = 0; i < menuPopupList.current.length; i++) {
        menuPopupList.current[i].setShow(menuPopupList.current[i].name === name)
      }
    }

    // Stop event propagation
    event.stopPropagation();
  }, [])


  //-----------------------------------------------------------------------------
  //
  // MENU CONTENT GENERATION
  //
  //-----------------------------------------------------------------------------

  // 1st level menu for large screens
  const MenuLargePopup = ({ name, items }) =>
  {
    // States
    const [show, setShow] = useState(false)

    // Register popup show/hide handler
    useEffect(() => {
      menuPopupList.current.push({ name: name, setShow: setShow })
    }, [setShow, name])

    // Quick abort
    if ((menuHovering.current !== name) || (show === false)) return null

    return <div className={"menu-popup-container menu-popup-container--expanded" + ((name === "User") ? " menu-popup-container--rightside": "")}
                role="presentation"
                onMouseOver={(event) => menuPopupHandler(event, name, false)}
                onFocus    ={(event) => menuPopupHandler(event, name, false)}
                onMouseOut ={(event) => menuPopupHandler(event, "", true)}
                onBlur     ={(event) => menuPopupHandler(event, "", true)}>
             {decorateMenuItems(items, MENU_POPUP, 'menu-popup')}
           </div>
  }


  // Function decorateMenuItems
  const decorateMenuItems = useCallback((itemList, type, className, innerClassName=undefined) =>
  {
    let objectList = [];
    let dividers = 0;

    if (itemList !== undefined)
    {
      itemList.forEach((item) => {
        // Push main menu object to list
        const DynamicLink = item.url.includes('http') ? ExternalLink : InternalLink
        if (type === MENU_MAIN)
        {
          let selected = (menuSelected === item.name)

          objectList.push(<div key={item.name} style={{ position: 'relative' }}>
                            <DynamicLink url={item.url}
                                         name={item.name}
                                         className={className}>
                              <button tabIndex="-1"
                                      aria-label={item.name}
                                      className={(selected) ? (className + '_div ' + className + '_div--selected') : (className + '_div')}
                                      onMouseOver={(event) => menuPopupHandler(event, item.name, false)}
                                      onFocus    ={(event) => menuPopupHandler(event, item.name, false)}
                                      onMouseOut ={(event) => menuPopupHandler(event, "", true)}
                                      onBlur     ={(event) => menuPopupHandler(event, "", true)}>
                                {(item.displayName) ? item.displayName : item.name}
                              </button>
                            </DynamicLink>
                            {(item.children) ? <MenuLargePopup name={item.name} items={item.children} /> : ''}
                          </div>);
        }

        // Push popup menu object to list
        else if (type === MENU_POPUP)
        {
          if (item.divider) {
            dividers += 1
            objectList.push(<div key={"DIVIDER-" + dividers} className="menu-popup_divider" />)
          }
          else {
            objectList.push(<div key={item.name} style={{ position: 'relative' }}>
                              <DynamicLink url={item.url}
                                           className={className}>
                                <button tabIndex="-1"
                                        aria-label={(item.displayName) ? item.displayName : item.name}
                                        style={{ background: 'none', border: 'none' }}>
                                  <div className={className + '_div'}>
                                    <b className={className + '_b'}>{(item.displayName) ? item.displayName : item.name}</b>
                                  </div>
                                </button>
                              </DynamicLink>
                             </div>);
          }
        }

        // Push small menu object to list
        else if (type === MENU_SMALL)
        {
          objectList.push(<React.Fragment key={item.name}>
                            <DynamicLink className={className}
                                         url={item.url}>
                              <div>
                                <b className={className}>{item.name}</b>
                              </div>
                            </DynamicLink>
                            {(item.children && innerClassName) ? decorateMenuItems(item.children, MENU_SMALL, innerClassName) : ''}
                          </React.Fragment>);
        }
      })
    }

    return objectList;
  }, [menuSelected, menuPopupHandler])


  // Function MainMenuItems
  const MainMenuItems = useMemo(() =>
  {
    return decorateMenuItems(menuStructure, MENU_MAIN, 'header-item');
  }, [decorateMenuItems])


  // Function SmallMainMenuItems
  const SmallMainMenuItems = useMemo(() =>
  {
    return decorateMenuItems(menuStructure, MENU_SMALL, 'small_menu_item', undefined);
  }, [decorateMenuItems])


  // Function CreateMenuItems
  const CreateMenuItems = useMemo(() =>
  {
    const createMenu = "Create";

    // Add logged in menu sub-items
    if (currentUser) {
      let createStructure = [{
                               name: createMenu,
                               displayName: (<>Create<RiArrowDropDownLine size="1.5em" style={{ marginRight: "-5px" }} /></>),
                               url: "/service/flow/",
                               children: [
                                 { name: "New Flow",
                                   displayName: (<><RiFlightTakeoffFill />&nbsp;&nbsp;New Flow</>),
                                   parent: createMenu,
                                   url: "/service/flow/"},
                                 { divider: true,
                                   url: "" },
                                 { name: "Datasets",
                                   parent: createMenu,
                                   url: "/service/datasets/"},
                                 { name: "Dashboards",
                                   parent: createMenu,
                                   url: "/service/dashboards/"},
                                 { divider: true,
                                   url: "" },
                                 { name: "Share",
                                   displayName: (<><RiShareForwardFill />&nbsp;&nbsp;Share</>),
                                   parent: createMenu,
                                   url: "/service/share/"}
                               ]
                             }]

      // Decorate menu item
      return decorateMenuItems(createStructure, MENU_MAIN, 'header-item');
    }

    // No current user
    else return null
  }, [currentUser, decorateMenuItems])


  // Function SmallCreateMenuItems
  const SmallCreateMenuItems = useMemo(() =>
  {
    const createMenu = "Create";

    // User logged in
    if (currentUser) {
      let createStructure = [{
                               name: createMenu,
                               url: "/service/flow/",
                               children: [
                                 { name: "New Flow",
                                   parent: createMenu,
                                   url: "/service/flow/"},
                                 { name: "Datasets",
                                   parent: createMenu,
                                   url: "/service/datasets/"},
                                 { name: "Dashboards",
                                   parent: createMenu,
                                   url: "/service/dashboards/"},
                                 { name: "Share",
                                   parent: createMenu,
                                   url: "/service/share/"}
                               ]
                             }]

      // Decorate menu item
      return decorateMenuItems(createStructure, MENU_SMALL, 'small_menu_item', 'small_child_item');
    }

    // No current user
    else return null
  }, [currentUser, decorateMenuItems])


  // Function LoginMenuItems
  const LoginMenuItems = useMemo(() =>
  {
    const userMenu = "User";

    // Create default login structure
    let loginStructure = [
      {
        name: userMenu,
        displayName: (<RiUser3Fill size="1.5em" />),
        url: (currentUser) ? "/service/" : "/service/login/",
      }
    ]

    // Add logged in menu sub-items
    if (currentUser) {
      loginStructure[0].children = [{ name: "My Space",
                                      parent: userMenu,
                                      url: "/service/"},
                                    { name: "Profile",
                                      parent: userMenu,
                                      url: "/service/profile/"},
                                    { divider: true,
                                      url: "" },
                                    { name: "Logout",
                                      displayName: (<><RiLogoutBoxRLine />&nbsp;&nbsp;Logout</>),
                                      parent: userMenu,
                                      url: "/service/logout/"}]
    }

    // Decorate menu item
    return decorateMenuItems(loginStructure, MENU_MAIN, 'header-item');
  }, [currentUser, decorateMenuItems])


  // Function LinkedInMenuItem
  const LinkedInMenuItem = useMemo(() =>
  {
    // Create default LinkedIn structure
    let linkedInStructure = [
      {
        name: "LinkedIn Page",
        displayName: (<RiLinkedinBoxFill size="2em" style={{ marginLeft: "-0.5em", marginRight: "-0.5em" }} />),
        url: "https://www.linkedin.com/showcase/sprucely-io/",
      }
    ]

    // Decorate menu item
    return decorateMenuItems(linkedInStructure, MENU_MAIN, 'header-item');
  }, [decorateMenuItems])


  // Function SmallLoginMenuItems
  const SmallLoginMenuItems = useMemo(() =>
  {
    let loginStructure

    // User logged in
    if (currentUser) {
      loginStructure = [{
                          name: "User",
                          url: "/service/",
                          children: [
                            { name: "My Space",
                              parent: "User",
                              url: "/service/"},
                            { name: "Profile",
                              parent: "User",
                              url: "/service/profile/"},
                            { name: "Logout",
                              parent: "User",
                              url: "/service/logout/"}
                          ]
                        }]
    }

    // No current user
    else {
      loginStructure = [{
                          name: "Login",
                          url: "/service/login/",
                        }]
    }

    return decorateMenuItems(loginStructure, MENU_SMALL, 'small_menu_item', 'small_child_item');
  }, [currentUser, decorateMenuItems])


  // Function SmallLinkedInMenuItem
  const SmallLinkedInMenuItem = useMemo(() =>
  {
    const linkedInStructure = [{
                                name: "LinkedIn Page",
                                url: "https://www.linkedin.com/showcase/sprucely-io/",
                              }]

    return decorateMenuItems(linkedInStructure, MENU_SMALL, 'small_menu_item', 'small_child_item');
  }, [decorateMenuItems])


  //-----------------------------------------------------------------------------
  //
  // SUBCOMPONENTS RENDERING
  //
  //-----------------------------------------------------------------------------

  // Menu button for small screens
  const MenuSmallButton = () =>
  {
    return <button className="menu-icon"
                   aria-label="Main menu"
                   style={{ display: "var(--sprucelyMenuSmall)" }}
                   onClick={(event) => { setMenuSmallExpanded(!menuSmallExpanded);
                                      event.stopPropagation(); }} >
             <div className="menu-icon-bar" />
             <div className="menu-icon-bar" />
             <div className="menu-icon-bar" />
           </button>
  }


  // Menu for small screens
  const MenuSmall = () =>
  {
    return <div className={menuSmallExpanded ? "menu menu--expanded" : "menu" }
                style={{ display: (menuSmallExpanded) ? "var(--sprucelyMenuSmall)" : "none" }}>
             {SmallMainMenuItems}
             {SmallLinkedInMenuItem}
             {SmallCreateMenuItems}
             {SmallLoginMenuItems}
           </div>
  }


  // Menu for large screens
  const MenuLarge = () =>
  {
    return <div className="header-menu"
                style={{ display: "var(--sprucelyMenuLarge)" }}>
             {MainMenuItems}
             {LinkedInMenuItem}
             <div className="header-menu-right">
               {CreateMenuItems}
               {LoginMenuItems}
             </div>
           </div>
  }


  // Page title and possibly with sub-menu
  const PageTitleWithMenu = useMemo(() =>
  {
    // Variable declarations
    let Icon = null

    // Load icons if the title matches a an icon set identifier
    menuIcons.forEach((menuIcon) =>
    {
      if (title && title.includes(menuIcon.name)) Icon = menuIcon.icon
    })

    // Render
    return <div className="title-container">
             <img src="/sprucely-title-splash.svg" alt="Sprucely title background" fetchpriority="high" style={{ position: "absolute" }} />
             <div className={"title-header" + ((pageLoaded) ? " title-header-loaded": "")}>
               {(Icon != null) ? <Icon alt={title} /> : null }
               {(titleChange) ?
                 (title) ? <div className="small-boxes-container center-block--vertical" style={{ gap: "0px" }}>
                             <div className={"h2-title h2-editable" + focusStyle}
                                  contentEditable="true"
                                  role="presentation"
                                  suppressContentEditableWarning="true"
                                  onFocus={(e) => { setFocusStyle(" h2-editable-focused") }}
                                  onKeyDown={(e) => {
                                    if ((e.key === 'Enter') || (e.key === 'Escape')) {
                                      e.preventDefault();
                                      if (e.key === 'Escape') e.currentTarget.textContent = title
                                      e.target.blur()
                                    }
                                  }}
                                  onBlur={(e) => { setFocusStyle(""); if (e.currentTarget.textContent !== title) titleChange(e.currentTarget.textContent) }}>
                               {title}
                             </div>
                             <RiEdit2Fill size="1.25em" color="var(--grey-dark)" />
                           </div>: null :
                 (title) ? <h1>{title}</h1> : null
               }
             </div>
           </div>
  }, [title, titleChange, pageLoaded, focusStyle, setFocusStyle])


  // Embedded logo
  const LogoEmbeddedImage = useMemo(() =>
  {
    return <a href="/" aria-label="Sprucely">
             <img className="header-logo"
                  alt="Sprucely"
                  src="/sprucely-logo.svg" />
           </a>
  }, [])


  //-----------------------------------------------------------------------------
  //
  // RENDERING
  //
  //-----------------------------------------------------------------------------
  return (<>
            <header>
              {LogoEmbeddedImage}
              <MenuLarge />
              <MenuSmall />
              <MenuSmallButton />
            </header>
            {(noTitleBlock !== true) && PageTitleWithMenu}
          </>)
})


// Property definitions
EmergenceHeader.propTypes = {
  title: PropTypes.string,
}


// Default properties
EmergenceHeader.defaultProps = {
  title: undefined,
}


// Default export
export default EmergenceHeader
